import type { Ref } from "vue";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { MultiPackCartLine } from "~/layers/base/stores/cartStore";
import { useGtmEvents } from "~/layers/base/composables/useGtmEvents";
import { scrollToCombineWith } from "~/layers/base/composables/useCombineWith";

interface ChildLine {
	PartNo: string;
	Quantity: number;
}

/** What the PDP has selected at the moment the button is pressed. */
export interface AddToCartSelection {
	/** Set for a quick-add from somewhere other than this product's own PDP. */
	wishProduct?: ProductItem;
	/** Pack children — `Quantity` scales with the parent quantity. */
	optionalItems?: ChildLine[];
	/** Extra children added as-is, not scaled by the parent quantity. */
	fixedOptionalItems?: ChildLine[];
	/** Multi-pack items; when set, the product on the page is not added. */
	multiPackItems?: MultiPackCartLine[];
	/** The same picks as catalogue products, for the analytics report. */
	multiPackPicks?: { product: ProductItem; quantity: number }[];
	/**
	 * Manual price for a line the customer configured, replacing the
	 * catalogue's. hogsta reads it off its own forked product store, which is
	 * why the values arrive from the template rather than being read here.
	 */
	priceOverride?: {
		Price?: number;
		Comment?: string | null;
		Info?: BasketItem["Info"];
		IsPriceManual?: boolean;
	};
}

/**
 * The PDP's add-to-cart press: builds the basket payload for whichever of the
 * three shapes applies (multi pack, managed-structure pack, plain product),
 * adds the Match & Buy add-ons, and reports to GTM.
 *
 * `selection` is read when the button is pressed, not when this is called.
 */
export const useAddToCartAction = (
	quantity: Ref<number>,
	selection: () => AddToCartSelection
) => {
	const cartStore = useCartStore();
	const productStore = useProductStore();
	const { selectedProduct } = storeToRefs(useProductStore());
	const { $toast } = useNuxtApp();
	const { t } = useI18n();
	const { addToCart, addItemsToCart } = useGtmEvents();
	const { buildDepositChild } = useDepositChild();

	/** `false` when there was nothing to add — no line reached the basket. */
	const addSelectionToCart = async (): Promise<boolean> => {
		const {
			wishProduct,
			optionalItems,
			fixedOptionalItems,
			multiPackItems,
			multiPackPicks,
			priceOverride
		} = selection();

		const product = wishProduct || selectedProduct.value;
		if (!product) return false;

		/*
			No line here names a price list.

			Every basket call carries the shopper's `pricelistSeed`, and reading the
			same basket back with a different seed returns different prices — so the
			seed is what prices a line. What used to be sent was
			`priceLists[0].Id`: whichever list the API happened to return first
			among those that apply, copied onto the parent, the pack contents and
			the deposit alike. Not a decision, just the first element of an array.

			Verified by adding to the basket without it.
		*/

		// Match & Buy add-ons are ordinary products picked on someone else's PDP:
		// their own top-level lines, removable on their own. Read before the
		// multi-pack branch — the two catalogue flags are independent, so a pack
		// can carry add-ons too.
		const addOns = fixedOptionalItems ?? [];

		// One call site for both paths: a forked cart store may take no options
		// at all, so every extra call site is another place that silently loses
		// `silent` the day someone forks again. Sequential, because both calls
		// create the basket when there is none.
		const addAddOns = async () => {
			if (!addOns.length) return;
			await cartStore.addItemsToCart(addOns, { silent: true });
		};

		if (multiPackItems?.length) {
			await cartStore.addMultiPackToCart(multiPackItems);
			await addAddOns();
			// Report the items, not the pack: it is never added, so reporting it
			// would put a never-purchased item in the funnel and leave the real
			// lines untracked. One event for the whole click.
			addItemsToCart(multiPackPicks ?? []);
			return true;
		}

		const name = wishProduct
			? product.Name
			: product.VariantName || product.Name;

		// The PDP parent is only a valid parametrics fallback for the PDP's own
		// product — a wishlist quick-add must not read whatever PDP was last
		// visited.
		const depositChild = await buildDepositChild(
			product,
			quantity.value,
			wishProduct ? undefined : productStore.products
		);

		// A pack's contents belong to its line: Norce children, not separately
		// removable.
		const packChildren = (optionalItems ?? []).map((child) => ({
			...child,
			Quantity: child.Quantity * quantity.value
		}));

		if (packChildren.length) {
			// Parent first, then children (Norce InsertBasketItemWithChildren).
			await cartStore.addManagedStructurePackToCart([
				{
					PartNo: product.PartNo,
					Quantity: quantity.value,
					Name: name
				},
				...packChildren,
				...(depositChild ? [depositChild] : [])
			]);
		} else {
			// `priceOverride` rides the plain line only: the template that needs
			// it has no packs, and a pack parent takes its price from the
			// structure.
			await cartStore.addToCart(
				{
					PartNo: product.PartNo,
					Quantity: quantity.value,
					Comment: null,
					Name: name,
					...(priceOverride ?? {})
				},
				depositChild
			);
		}

		await addAddOns();

		addToCart(product);

		return true;
	};

	/**
	 * Every basket call rethrows on failure (the store only handles a stale
	 * basket), so the whole sequence runs inside one guard: without it a failed
	 * add left the spinner off, nothing added and nothing said.
	 */
	const handleAddToCart = async () => {
		// Read once, at the press: the awaits below give the PDP time to change
		// its mind about what is selected.
		const { wishProduct } = selection();

		let added = false;
		try {
			added = await addSelectionToCart();
		} catch (error) {
			console.error("[add-to-cart] could not add the product", error);
			$toast.error(t("add_to_cart_failed"));
			return;
		}

		// Only when a line really went in, and never for a wishlist add — that
		// button lives outside the product page.
		if (added && !wishProduct) scrollToCombineWith();
	};

	return { handleAddToCart };
};
